import { useEffect, useRef } from 'react';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('animate-in');
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

export default function WhyChooseUs() {
  const imageRef = useScrollReveal();
  const contentRef = useScrollReveal();
  const destRef = useScrollReveal(0.1);

  const features = [
    'Small Group Sizes (Max 6 Students)',
    'Safety First Approach',
    'Local Ocean Knowledge',
    'IKO & ISA Certified Instructors'
  ];

  return (
    <section id="why-choose-us" className="py-24 bg-sand-100">
      <style>{`
        .reveal-left { opacity: 0; transform: translateX(-40px); transition: opacity 0.8s ease, transform 0.8s ease; }
        .reveal-left.animate-in { opacity: 1; transform: translateX(0); }
        .reveal-right { opacity: 0; transform: translateX(40px); transition: opacity 0.8s ease, transform 0.8s ease; transition-delay: 0.15s; }
        .reveal-right.animate-in { opacity: 1; transform: translateX(0); }
        .reveal-up { opacity: 0; transform: translateY(40px); transition: opacity 0.8s ease, transform 0.8s ease; }
        .reveal-up.animate-in { opacity: 1; transform: translateY(0); }
      `}</style>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Image */}
          <div ref={imageRef} className="reveal-left relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/ae2d3209f0ef497cda38ed4960c1af08.jpg"
                alt="Surf Instructor Teaching"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-sand-50 rounded-2xl shadow-lg p-5 max-w-xs border border-sand-200">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-full">
                  <i className="ri-trophy-fill text-2xl text-teal-600"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">500+</p>
                  <p className="text-sm text-gray-500">Élèves satisfaits</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div ref={contentRef} className="reveal-right lg:pl-8">
            <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-4">Certifiés &amp; Locaux</p>
            <h2 className="text-5xl font-bold text-gray-900 mb-3">
              <span className="text-teal-600">10+</span> Ans
            </h2>
            <p className="text-xl text-gray-700 font-medium mb-6">
              à enseigner les vagues d'Essaouira
            </p>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">
              Nous ne sommes pas que des instructeurs — nous sommes des passionnés de l'océan qui vivent à Essaouira. Notre équipe allie certifications internationales et connaissance locale pour vous offrir la meilleure expérience possible sur l'eau.
            </p>
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-500"></i>
                  </div>
                  <span className="text-sm text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
            <button className="border-2 border-gray-900 text-gray-900 px-8 py-3 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-gray-900 hover:text-white transition-all cursor-pointer">
              Notre équipe
            </button>
          </div>
        </div>

        {/* Essaouira Destination Section */}
        <div ref={destRef} className="reveal-up mt-24 bg-gray-900 rounded-2xl overflow-hidden shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-12 lg:p-16">
              <p className="text-xs font-bold tracking-widest text-teal-400 uppercase mb-4">Destination Mondiale</p>
              <h2 className="text-4xl font-bold text-white mb-5 leading-tight">
                Pourquoi Essaouira est parfaite pour le surf
              </h2>
              <p className="text-sm text-white/70 mb-8 leading-relaxed">
                Avec ses houles atlantiques constantes, ses vents alizés réguliers et son soleil toute l'année, Essaouira offre des conditions idéales pour le surf et le kitesurf. Les grandes plages de sable et la chaleur marocaine en font la destination ultime.
              </p>
              <button className="border-2 border-white/40 text-white px-8 py-3 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-white hover:text-gray-900 transition-all cursor-pointer">
                Découvrir Essaouira
              </button>
            </div>
            <div className="grid grid-cols-2 gap-0">
              <div className="bg-white/5 p-10 flex flex-col justify-center border-r border-white/10">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-xl mb-4">
                  <i className="ri-sun-line text-2xl text-teal-400"></i>
                </div>
                <p className="text-4xl font-bold text-white mb-1">300+</p>
                <p className="text-sm text-white/60">Jours de soleil par an</p>
              </div>
              <div className="bg-white/5 p-10 flex flex-col justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-xl mb-4">
                  <i className="ri-windy-line text-2xl text-orange-400"></i>
                </div>
                <p className="text-4xl font-bold text-white mb-1">Parfait</p>
                <p className="text-sm text-white/60">Vent toute l'année</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
